import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CommandCenter from "@/pages/CommandCenter";
import Leads from "@/pages/Leads";
import LeadHunt from "@/pages/LeadHunt";
import AISwarm from "@/pages/crm/AISwarm";
import SwarmQueue from "@/pages/crm/SwarmQueue";
import QuoraInbox from "@/pages/crm/QuoraInbox";
import EmailCenter from "@/pages/crm/EmailCenter";
import WhatsAppCenter from "@/pages/crm/WhatsAppCenter";
import Procurement from "@/pages/finance/Procurement";
import Leaderboard from "@/pages/Leaderboard";
import DSRReport from "@/pages/reports/DSRReport";
import Catalog from "@/pages/Catalog";
import Proposals from "@/pages/Proposals";
import ProposalBuilder from "@/pages/ProposalBuilder";
import PublicProposal from "@/pages/PublicProposal";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import Companies from "@/pages/finance/Companies";
import Contacts from "@/pages/finance/Contacts";
import WorkOrders from "@/pages/finance/WorkOrders";
import WorkOrderDetail from "@/pages/finance/WorkOrderDetail";
import Salaries from "@/pages/finance/Salaries";
import OperatingExpenses from "@/pages/finance/OperatingExpenses";
import FinanceAnalysis from "@/pages/finance/FinanceAnalysis";
import MISReport from "@/pages/mis/MISReport";
import MISLog from "@/pages/mis/MISLog";
import MISEntities from "@/pages/mis/MISEntities";
import MISServices from "@/pages/mis/MISServices";
import MISDailyLog from "@/pages/mis/MISDailyLog";
import Tracker from "@/pages/tracker/Tracker";
import Onboarding from "@/pages/hr/Onboarding";
import PerformanceAdmin from "@/pages/hr/PerformanceAdmin";
import Letters from "@/pages/hr/Letters";
import Resumes from "@/pages/hr/Resumes";
import Policies from "@/pages/hr/Policies";
import Vouchers from "@/pages/finance/Vouchers";
import VoucherAI from "@/pages/finance/VoucherAI";
import Billing from "@/pages/finance/Billing";
import Purchases from "@/pages/finance/Purchases";
import Inventory from "@/pages/finance/Inventory";
import GroupHealth from "@/pages/finance/GroupHealth";
import SyncSettings from "@/pages/finance/SyncSettings";
import Snapshot from "@/pages/finance/Snapshot";
import DispatchBoard from "@/pages/dispatch/DispatchBoard";
import MISTargets from "@/pages/mis/MISTargets";
import AdminUsers from "@/pages/admin/AdminUsers";

const CRM = ["crm", "viewer"];
const FIN = ["finance", "viewer"];
const MIS = ["mis", "viewer"];
const TRACKER = ["tracker", "viewer", "admin"];
const HR = ["hr", "viewer"];
const ANY = ["crm", "finance", "mis", "hr", "tracker", "viewer", "admin"];
const ADMIN = ["admin"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/dashboard" element={<ProtectedRoute requireRoles={ANY}><CommandCenter /></ProtectedRoute>} />
      <Route path="/dashboard/sales" element={<ProtectedRoute requireRoles={CRM}><Dashboard /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute requireRoles={CRM}><Leads /></ProtectedRoute>} />
      <Route path="/leads/hunt" element={<ProtectedRoute requireRoles={CRM}><LeadHunt /></ProtectedRoute>} />
      <Route path="/swarm" element={<ProtectedRoute requireRoles={CRM}><AISwarm /></ProtectedRoute>} />
      <Route path="/swarm/queue" element={<ProtectedRoute requireRoles={CRM}><SwarmQueue /></ProtectedRoute>} />
      <Route path="/swarm/quora-inbox" element={<ProtectedRoute requireRoles={CRM}><QuoraInbox /></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute requireRoles={CRM}><EmailCenter /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute requireRoles={CRM}><WhatsAppCenter /></ProtectedRoute>} />
      <Route path="/procurement" element={<ProtectedRoute requireRoles={CRM}><Procurement /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute requireRoles={CRM}><Leaderboard /></ProtectedRoute>} />
      <Route path="/reports/dsr" element={<ProtectedRoute requireRoles={CRM}><DSRReport /></ProtectedRoute>} />
      <Route path="/catalog" element={<ProtectedRoute requireRoles={CRM}><Catalog /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute requireRoles={CRM}><Proposals /></ProtectedRoute>} />
      <Route path="/proposals/new" element={<ProtectedRoute requireRoles={CRM}><ProposalBuilder /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute requireRoles={CRM}><ProposalBuilder /></ProtectedRoute>} />
      {/* Finance */}
      <Route path="/finance" element={<ProtectedRoute requireRoles={FIN}><FinanceDashboard /></ProtectedRoute>} />
      <Route path="/finance/companies" element={<ProtectedRoute requireRoles={FIN}><Companies /></ProtectedRoute>} />
      <Route path="/finance/contacts" element={<ProtectedRoute requireRoles={FIN}><Contacts /></ProtectedRoute>} />
      <Route path="/finance/work-orders" element={<ProtectedRoute requireRoles={FIN}><WorkOrders /></ProtectedRoute>} />
      <Route path="/finance/work-orders/:id" element={<ProtectedRoute requireRoles={FIN}><WorkOrderDetail /></ProtectedRoute>} />
      <Route path="/finance/salaries" element={<ProtectedRoute requireRoles={FIN}><Salaries /></ProtectedRoute>} />
      <Route path="/finance/operating-expenses" element={<ProtectedRoute requireRoles={FIN}><OperatingExpenses /></ProtectedRoute>} />
      <Route path="/finance/purchases" element={<ProtectedRoute requireRoles={FIN}><Purchases /></ProtectedRoute>} />
      <Route path="/finance/inventory" element={<ProtectedRoute requireRoles={FIN}><Inventory /></ProtectedRoute>} />
      <Route path="/finance/group-health" element={<ProtectedRoute requireRoles={FIN}><GroupHealth /></ProtectedRoute>} />
      <Route path="/mis/targets" element={<ProtectedRoute requireRoles={["mis","finance","admin"]}><MISTargets /></ProtectedRoute>} />
      <Route path="/finance/analysis" element={<ProtectedRoute requireRoles={FIN}><FinanceAnalysis /></ProtectedRoute>} />
      {/* MIS */}
      <Route path="/mis" element={<ProtectedRoute requireRoles={MIS}><MISReport /></ProtectedRoute>} />
      <Route path="/mis/log" element={<ProtectedRoute requireRoles={MIS}><MISLog /></ProtectedRoute>} />
      <Route path="/mis/daily" element={<ProtectedRoute requireRoles={MIS}><MISDailyLog /></ProtectedRoute>} />
      <Route path="/mis/services" element={<ProtectedRoute requireRoles={MIS}><MISServices /></ProtectedRoute>} />
      <Route path="/mis/entities" element={<ProtectedRoute requireRoles={MIS}><MISEntities /></ProtectedRoute>} />
      {/* Shelves-Track */}
      <Route path="/tracker" element={<ProtectedRoute requireRoles={TRACKER}><Tracker /></ProtectedRoute>} />
      {/* HR */}
      <Route path="/hr/onboarding" element={<ProtectedRoute requireRoles={HR}><Onboarding /></ProtectedRoute>} />
      <Route path="/hr/performance" element={<ProtectedRoute requireRoles={HR}><PerformanceAdmin /></ProtectedRoute>} />
      <Route path="/hr/letters" element={<ProtectedRoute requireRoles={HR}><Letters /></ProtectedRoute>} />
      <Route path="/hr/resumes" element={<ProtectedRoute requireRoles={HR}><Resumes /></ProtectedRoute>} />
      <Route path="/hr/policies" element={<ProtectedRoute requireRoles={ANY}><Policies /></ProtectedRoute>} />
      {/* Finance Vouchers (anyone can create; HR/admin approves) */}
      <Route path="/finance/vouchers" element={<ProtectedRoute requireRoles={ANY}><Vouchers /></ProtectedRoute>} />
      <Route path="/finance/voucher-ai" element={<ProtectedRoute requireRoles={FIN}><VoucherAI /></ProtectedRoute>} />
      <Route path="/finance/billing" element={<ProtectedRoute requireRoles={FIN}><Billing /></ProtectedRoute>} />
      <Route path="/finance/snapshot" element={<ProtectedRoute requireRoles={ADMIN}><Snapshot /></ProtectedRoute>} />
      <Route path="/finance/sync-settings" element={<ProtectedRoute requireRoles={ADMIN}><SyncSettings /></ProtectedRoute>} />
      <Route path="/dispatch" element={<ProtectedRoute requireRoles={FIN}><DispatchBoard /></ProtectedRoute>} />
      {/* Admin */}
      <Route path="/admin/users" element={<ProtectedRoute requireRoles={ADMIN}><AdminUsers /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CompanyProvider>
            <AppRouter />
            <Toaster position="top-right" richColors closeButton />
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
